import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import api from '../services/api';
import { 
  ArrowLeft, Check, Save, Loader2,
  GraduationCap, Briefcase, Building2, Globe2, Heart, Users
} from 'lucide-react';

const TENANT_TYPES = [
  { id: 'student', label: 'Student', icon: GraduationCap, desc: 'HBO/WO studenten' },
  { id: 'starter', label: 'Starter', icon: Briefcase, desc: 'Net afgestudeerd, eerste baan' },
  { id: 'professional', label: 'Professional', icon: Building2, desc: 'Werkend met vast inkomen' },
  { id: 'expat', label: 'Expat', icon: Globe2, desc: 'Internationaal talent' },
  { id: 'koppel', label: 'Koppel', icon: Heart, desc: 'Samen op zoek' },
  { id: 'any', label: 'Iedereen', icon: Users, desc: 'Geen voorkeur' },
];

const AGE_RANGES = [
  { id: '18-25', label: '18-25 jaar' },
  { id: '25-35', label: '25-35 jaar' },
  { id: '35-50', label: '35-50 jaar' },
  { id: '50+', label: '50+ jaar' },
  { id: 'any', label: 'Alle leeftijden' },
];

const GENDER_OPTIONS = [
  { id: 'man', label: 'Man' },
  { id: 'vrouw', label: 'Vrouw' },
  { id: 'any', label: 'Geen voorkeur' },
];

const VerhuurderVoorkeurenPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    preferredTenantTypes: [],
    preferredAgeRange: 'any',
    preferredGender: 'any'
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Only landlords can access this page
    if (user?.userType === 'huurder') {
      navigate('/dashboard');
      return;
    }
    loadPreferences();
  }, [isAuthenticated, authLoading, navigate, user]);

  const loadPreferences = async () => {
    try {
      const res = await api.get('/landlord/preferences');
      if (res.data.preferences) {
        setPreferences({
          preferredTenantTypes: res.data.preferences.preferredTenantTypes || [],
          preferredAgeRange: res.data.preferences.preferredAgeRange || 'any',
          preferredGender: res.data.preferences.preferredGender || 'any'
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTenantType = (id) => {
    if (id === 'any') {
      setPreferences(prev => ({ ...prev, preferredTenantTypes: ['any'] }));
    } else {
      setPreferences(prev => {
        const filtered = prev.preferredTenantTypes.filter(t => t !== 'any');
        if (filtered.includes(id)) {
          return { ...prev, preferredTenantTypes: filtered.filter(t => t !== id) };
        }
        return { ...prev, preferredTenantTypes: [...filtered, id] };
      });
    }
  };

  const handleSave = async () => {
    if (preferences.preferredTenantTypes.length === 0) {
      toast({ title: 'Selecteer minstens één type huurder', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await api.post('/landlord/preferences', preferences);
      toast({ title: 'Voorkeuren opgeslagen!', description: 'Je ontvangt nu relevantere huurders' });
      navigate('/verhuurder/dashboard');
    } catch (error) {
      toast({ title: 'Er ging iets mis', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="fixed inset-0 bg-[#F7F7F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#2E7D32] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F7]" data-testid="verhuurder-voorkeuren">
      <Toaster />

      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] sticky top-0 z-10">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)}>
            <ArrowLeft className="w-6 h-6 text-[#2A2A2A]" />
          </button>
          <SwipeMatchLogo size="small" variant="color" />
          <div className="w-6" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-4 py-6 pb-32">
        <div className="mb-8">
          <h1 className="text-3xl font-black text-[#2A2A2A] mb-2">Huurdersvoorkeuren</h1>
          <p className="text-[#9E9E9E]">Bepaal welke huurders jij wilt ontvangen</p>
        </div>

        {/* Tenant Types */}
        <div className="mb-8">
          <label className="text-sm font-bold text-[#2A2A2A] mb-4 block">Type huurder</label>
          <div className="grid grid-cols-2 gap-3">
            {TENANT_TYPES.map((type) => {
              const Icon = type.icon;
              const isSelected = preferences.preferredTenantTypes.includes(type.id);
              return (
                <button
                  key={type.id}
                  onClick={() => toggleTenantType(type.id)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all relative ${
                    isSelected
                      ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                      : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                    isSelected ? 'bg-[#2E7D32] text-white' : 'bg-[#F7F7F7] text-[#9E9E9E]'
                  }`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-[#2A2A2A]">{type.label}</h3>
                  <p className="text-xs text-[#9E9E9E] mt-1">{type.desc}</p>
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-5 h-5 bg-[#2E7D32] rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Age Range */}
        <div className="mb-8">
          <label className="text-sm font-bold text-[#2A2A2A] mb-4 block">Leeftijd voorkeur</label>
          <div className="grid grid-cols-2 gap-2">
            {AGE_RANGES.map((range) => (
              <button
                key={range.id}
                onClick={() => setPreferences(prev => ({ ...prev, preferredAgeRange: range.id }))}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  preferences.preferredAgeRange === range.id
                    ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium ${
                  preferences.preferredAgeRange === range.id ? 'text-[#2E7D32]' : 'text-[#2A2A2A]'
                }`}>
                  {range.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Gender */}
        <div className="mb-8">
          <label className="text-sm font-bold text-[#2A2A2A] mb-4 block">Geslacht voorkeur</label>
          <div className="grid grid-cols-3 gap-2">
            {GENDER_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => setPreferences(prev => ({ ...prev, preferredGender: option.id }))}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  preferences.preferredGender === option.id
                    ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                    : 'border-[#E5E5E5] bg-white'
                }`}
              >
                <span className={`font-medium ${
                  preferences.preferredGender === option.id ? 'text-[#2E7D32]' : 'text-[#2A2A2A]'
                }`}>
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E5E5] p-4">
        <div className="max-w-xl mx-auto">
          <Button
            onClick={handleSave}
            disabled={saving || preferences.preferredTenantTypes.length === 0}
            className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold text-lg"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Voorkeuren opslaan
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VerhuurderVoorkeurenPage;
